import { useEffect, useMemo } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  useAllPOAStatementConsentsRecommendationsQuery,
  usePOARecommendationsQuery,
  usePOAStatementConsentsStatusQuery,
  type usePOAStatementConsentsRecommendationsQuery,
} from "@services/powersOfAttorney/queries";
import {
  POAStatementConsentStateId,
  type IPOARecommendationsResponse,
  type IPOAStatementConsentsRecommendationsRequest,
} from "@services/powersOfAttorney/types";
import { POAServiceProviderSource } from "./types";

interface IuseReccomendationQueryReturn {
  useRecommendationQueryFn:
    | typeof usePOARecommendationsQuery
    | typeof usePOAStatementConsentsRecommendationsQuery
    | typeof useAllPOAStatementConsentsRecommendationsQuery
    | undefined;
  reccomendationSource?: POAServiceProviderSource;
  useFallbackRecommendationQueryFn:
    | typeof usePOARecommendationsQuery
    | typeof useAllPOAStatementConsentsRecommendationsQuery;
  isReccomendationQueryLoading: boolean;
}

interface IuseReccomendationQueryFnReturn {
  queryFn: (
    data: IPOAStatementConsentsRecommendationsRequest,
  ) => UseQueryResult<IPOARecommendationsResponse, Error>;
  reccomendationSource: POAServiceProviderSource;
}

export const useReccomendationQuery = (
  powerOfAttorneyLedgerConsentId?: string,
  shouldFetchAllRecommendations?: boolean,
): IuseReccomendationQueryReturn => {
  const queryClient = useQueryClient();
  const { data: consentStatus, isLoading: isConsentStatusLoading } =
    usePOAStatementConsentsStatusQuery(
      {
        powerOfAttorneyLedgerConsentId: powerOfAttorneyLedgerConsentId ?? "",
      },
      Boolean(powerOfAttorneyLedgerConsentId),
    );

  useEffect(() => {
    let timer = null;

    if (
      consentStatus?.succeeded &&
      consentStatus.powerOfAttorneyStatementConsentStateId ===
        POAStatementConsentStateId.wait
    ) {
      timer = setInterval(() => {
        void queryClient.refetchQueries({
          queryKey: ["powersOfAttorney", "statementConsents", "status"],
        });
      }, 3000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [consentStatus, queryClient]);

  const useRecommendationQueryFn = useMemo(():
    | IuseReccomendationQueryFnReturn
    | undefined => {
    if (shouldFetchAllRecommendations) {
      return {
        queryFn: useAllPOAStatementConsentsRecommendationsQuery,
        reccomendationSource: POAServiceProviderSource.StatementRecommendation,
      };
    }

    if (!consentStatus && isConsentStatusLoading) {
      return undefined;
    }

    if (!consentStatus?.succeeded) {
      return {
        queryFn: usePOARecommendationsQuery,
        reccomendationSource: POAServiceProviderSource.GenericRecommendation,
      };
    }

    switch (consentStatus.powerOfAttorneyStatementConsentStateId) {
      case POAStatementConsentStateId.success:
        return {
          queryFn: useAllPOAStatementConsentsRecommendationsQuery,
          reccomendationSource:
            POAServiceProviderSource.StatementRecommendation,
        };
      case POAStatementConsentStateId.failed:
        return {
          queryFn: usePOARecommendationsQuery,
          reccomendationSource: POAServiceProviderSource.GenericRecommendation,
        };
      default:
        return undefined;
    }
  }, [consentStatus, isConsentStatusLoading, shouldFetchAllRecommendations]);

  return {
    useRecommendationQueryFn: useRecommendationQueryFn?.queryFn,
    reccomendationSource: useRecommendationQueryFn?.reccomendationSource,
    useFallbackRecommendationQueryFn: usePOARecommendationsQuery,
    isReccomendationQueryLoading: Boolean(
      !useReccomendationQuery ||
        (consentStatus?.succeeded &&
          consentStatus.powerOfAttorneyStatementConsentStateId ===
            POAStatementConsentStateId.wait),
    ),
  };
};
